import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useUserSolutions, Solution } from "@/hooks/useSolutions";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLink, FileText, MoreVertical, Eye, Pencil, Coins, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface SolutionsListProps {
  userId?: string;
}

interface SolutionItemProps {
  solution: Solution;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

const SolutionItem = ({ solution, onDelete, isDeleting }: SolutionItemProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleViewDetails = () => {
    navigate(`/solution/${solution.id}`);
  };

  const handleEdit = () => {
    navigate(`/edit-solution/${solution.id}`);
  };

  const handleMint = () => {
    toast({
      title: "Coming Soon",
      description: "Web3 minting capability will be available soon!",
    });
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    onDelete(solution.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <div className="border rounded-lg p-4">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="font-semibold">{solution.title}</h3>
            <p className="text-sm text-muted-foreground">{solution.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={solution.status === 'consumed' ? 'default' : 'secondary'}>
              {solution.status}
            </Badge>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={5}>
                <DropdownMenuItem onClick={handleViewDetails}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEdit}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleMint}>
                  <Coins className="h-4 w-4 mr-2" />
                  Mint
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm mt-3">
          <div>
            <span className="text-muted-foreground">Agency:</span> {solution.agency}
          </div>
          <div>
            <span className="text-muted-foreground">Type:</span> {solution.commercial_type}
          </div>
          <div>
            <span className="text-muted-foreground">Price:</span> ${solution.price.toFixed(2)}
          </div>
          {solution.is_bounty && solution.bounty_amount && (
            <div>
              <span className="text-muted-foreground">Bounty:</span> ${solution.bounty_amount.toFixed(2)}
            </div>
          )}
          {solution.file_url && (
            <div className="col-span-2 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              <a 
                href={solution.file_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
              >
                {solution.file_name || 'View Document'}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Solution</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{solution.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const SolutionsList = ({ userId }: SolutionsListProps = {}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const effectiveUserId = userId || user?.id;
  const { data: solutions, isLoading } = useUserSolutions(effectiveUserId);

  const deleteMutation = useMutation({
    mutationFn: async (solutionId: string) => {
      const { error } = await supabase
        .from('solutions')
        .delete()
        .eq('id', solutionId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSolutions'] });
      toast({
        title: "Solution Deleted",
        description: "The solution has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete solution. Please try again.",
        variant: "destructive",
      });
      console.error('Delete error:', error);
    },
  });

  const handleDelete = (solutionId: string) => {
    deleteMutation.mutate(solutionId);
  };

  const allSolutions = solutions || [];
  const consumedSolutions = allSolutions.filter(s => s.status === 'consumed');
  const publishedSolutions = allSolutions.filter(s => s.status === 'published');

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Solutions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading solutions...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Solutions</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all">All Solutions ({allSolutions.length})</TabsTrigger>
            <TabsTrigger value="consumed">Consumed ({consumedSolutions.length})</TabsTrigger>
            <TabsTrigger value="network">Network Included ({publishedSolutions.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-0 p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-4 p-4">
                {allSolutions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No solutions yet</p>
                ) : (
                  allSolutions.map((solution) => (
                    <SolutionItem 
                      key={solution.id} 
                      solution={solution} 
                      onDelete={handleDelete}
                      isDeleting={deleteMutation.isPending}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="consumed" className="mt-0 p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-4 p-4">
                {consumedSolutions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No consumed solutions yet</p>
                ) : (
                  consumedSolutions.map((solution) => (
                    <SolutionItem 
                      key={solution.id} 
                      solution={solution} 
                      onDelete={handleDelete}
                      isDeleting={deleteMutation.isPending}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="network" className="mt-0 p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-4 p-4">
                {publishedSolutions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No solutions with network members yet</p>
                ) : (
                  publishedSolutions.map((solution) => (
                    <div key={solution.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold">{solution.title}</h3>
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" sideOffset={5}>
                            <DropdownMenuItem onClick={() => window.location.href = `/solution/${solution.id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.location.href = `/edit-solution/${solution.id}`}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toast({ title: "Coming Soon", description: "Web3 minting capability will be available soon!" })}>
                              <Coins className="h-4 w-4 mr-2" />
                              Mint
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(solution.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {solution.solution_network_members && solution.solution_network_members.length > 0 ? (
                        <div className="space-y-2">
                          {solution.solution_network_members.map((member) => (
                            <div key={member.user_id} className="flex justify-between items-center p-2 bg-muted rounded">
                              <div>
                                <p className="font-medium">{member.profiles.display_name}</p>
                                {member.gib_nifty && (
                                  <p className="text-xs text-muted-foreground">GibNifty: {member.gib_nifty}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">{member.ownership_stake}%</p>
                                <p className="text-xs text-muted-foreground">Ownership</p>
                              </div>
                            </div>
                          ))}
                          {solution.is_bounty && solution.bounty_amount && (
                            <div className="mt-3 p-2 bg-primary/10 rounded">
                              <p className="text-sm font-semibold">Solution Bounty: ${solution.bounty_amount.toFixed(2)}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No network members yet</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SolutionsList;
